import React from 'react';
import { Link, Redirect } from 'react-router-dom';
import { UserCreateData } from 'models/UserCreateData';
import Locale from 'localization/Localization';
import { Spinner } from '../common/Spinner';
import { ResultMessageView, ResultMessage, ResultStyle } from '../common/ResultMessageView';
import AppServices from 'services/AppServices';
import { ApiError } from 'models/ApiError';
import { isValidEmailAddress } from 'util/Utils';
import { getCurrentLanguage } from 'components/contexts/LocalizationContext';

interface SignupFormProps {
	onSuccessFn: () => any;
}

interface SignupFormState {
	inputsDisabled: boolean;
	loginResult: ResultMessage | undefined;
	redirect?: string;
	userEmail: string;
	userPassword: string;
	userPasswordConfirm: string;
	eulaAccepted: boolean;
}

export class SignupForm extends React.Component<SignupFormProps, SignupFormState>
{
	constructor(props: SignupFormProps) {
		super(props);

		this.state = {
			inputsDisabled: false,
			loginResult: undefined,
			userEmail: '',
			userPassword: '',
			userPasswordConfirm: '',
			eulaAccepted: false
		};

		this.onUserEmailChanged = this.onUserEmailChanged.bind(this);
		this.onUserPasswordChanged = this.onUserPasswordChanged.bind(this);
		this.onUserPasswordConfirmChanged = this.onUserPasswordConfirmChanged.bind(this);
		this.onEulaAcceptedToggled = this.onEulaAcceptedToggled.bind(this);
		this.onSubmitClick = this.onSubmitClick.bind(this);
	}

	public render() {
		return <div className='login-container'>
			<h2>{Locale.get('signup_header')}</h2>
			{this.state.redirect && <Redirect to={this.state.redirect} />}
			{this.state.inputsDisabled && <Spinner />}

			{this.state.inputsDisabled === false &&
				<form onSubmit={this.onSubmitClick}>

					<label className='input-label' htmlFor='loginUserEmail'>{Locale.get('signup_emailLabel')}</label>
					<input type='email' id='loginUserEmail' onChange={this.onUserEmailChanged} disabled={this.state.inputsDisabled} />
					<label className='input-label' htmlFor='loginUserPassword'>{Locale.get('signup_passwordLabel')}</label>
					<input type='password' id='loginUserPassword' onChange={this.onUserPasswordChanged} disabled={this.state.inputsDisabled} />
					<label className='input-label' htmlFor='loginUserPasswordConfirm'>{Locale.get('signup_passwordConfirmLabel')}</label>
					<input type='password' id='loginUserPasswordConfirm' onChange={this.onUserPasswordConfirmChanged} disabled={this.state.inputsDisabled} />

					<div className='pad1em'>
						<Link to={'/eula/' + getCurrentLanguage()} target="_blank">{Locale.get('signup_eulaLink')}</Link>
					</div>

					{/* <div>
						<label className='input-label' htmlFor='loginUserEmail'>{Locale.get('signup_region')}</label>
						<input type='radio' id='rowRadioBtn' value={Region.Row} onChange={this.onUserRegionChanged} checked={this.state.userRegion === Region.Row} />
						<label htmlFor='rowRadioBtn'>{Locale.get('signup_row')}</label><br />
						<input type='radio' id='usaRadioBtn' value={Region.Usa} onChange={this.onUserRegionChanged} checked={this.state.userRegion === Region.Usa} />
						<label htmlFor='usaRadioBtn'>{Locale.get('signup_usa')}</label>
					</div> */}

					<div className='checkbox-container'>
						<input type='checkbox' id='eulaAccepted' onChange={this.onEulaAcceptedToggled} disabled={this.state.inputsDisabled} />
						<label className='checkbox-label' htmlFor='eulaAccepted'>{Locale.get('signup_acceptEulaLabel')}</label>
					</div>
					<div className='submit-container'>
						<input type='submit' value={Locale.get('signup_submitButton')} disabled={this.state.inputsDisabled} />
					</div>
				</form>
			}

			<ResultMessageView message={this.state.loginResult} />
		</div>;
	}

	private onUserEmailChanged(event: React.ChangeEvent<HTMLInputElement>) { this.setState({ userEmail: event.target.value }); }

	private onUserPasswordChanged(event: React.ChangeEvent<HTMLInputElement>) { this.setState({ userPassword: event.target.value }); }

	private onUserPasswordConfirmChanged(event: React.ChangeEvent<HTMLInputElement>) { this.setState({ userPasswordConfirm: event.target.value }); }

	// private onUserRegionChanged(event: React.ChangeEvent<HTMLInputElement>) {
	// 	try {
	// 		let typedRegionStr = event.target.value as keyof typeof Region;
	// 		let region: Region = Region[typedRegionStr];

	// 		this.setState({ userRegion: region });
	// 	}
	// 	catch (ex) {
	// 		console.log("onUserRegionChanged() failed to parse input to Region");
	// 	}
	// }

	private onEulaAcceptedToggled(event: React.ChangeEvent<HTMLInputElement>) { this.setState({ eulaAccepted: event.target.checked }); }

	private onSubmitClick(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();

		// Validate
		if (this.state.userEmail.trim().length === 0 ||
			this.state.userPassword.trim().length === 0 ||
			this.state.userPasswordConfirm.trim().length === 0) {
			this.setState({
				loginResult: new ResultMessage(Locale.get('signup_errorEmptyField'), ResultStyle.Error)
			});
			return;
		}

		if (isValidEmailAddress(this.state.userEmail.trim()) === false) {
			this.setState({
				loginResult: new ResultMessage(Locale.get('signup_errorInvalidEmail'), ResultStyle.Error)
			});
			return;
		}

		if (this.state.userPassword !== this.state.userPasswordConfirm) {
			this.setState({
				loginResult: new ResultMessage(Locale.get('signup_errorPasswordsMismatch'), ResultStyle.Error)
			});
			return;
		}

		if (this.state.eulaAccepted === false) {
			this.setState({
				loginResult: new ResultMessage(Locale.get('signup_errorEulaNotAccepted'), ResultStyle.Error)
			});
			return;
		}

		// Signup
		let user: UserCreateData = {
			email: this.state.userEmail,
			password: this.state.userPassword,
			language: getCurrentLanguage()
		}

		this.setState({
			inputsDisabled: true,
			loginResult: undefined
		});

		AppServices.userService.createUser(user)
			.then(() => {
				this.setState({
					loginResult: new ResultMessage("Success", ResultStyle.Success)
				});

				if (this.props.onSuccessFn)
					this.props.onSuccessFn();
				else
					setTimeout(() => this.setState({ redirect: '/login' }), 2000);
			})
			.catch((reason) => {
				const message = Locale.get(
					reason === ApiError.EmailAddressInUse ? 'signup_errorEmailInUse' :
						reason === ApiError.MissingParameter ? 'signup_errorEmptyField' :
							reason === ApiError.EmailAddressNotValid ? 'signup_errorInvalidEmail' :
								reason === ApiError.PasswordDoesNotMeetRequirements ? 'signup_errorPasswordRequirements' :
									'signup_errorSignupFailed');

				this.setState({
					inputsDisabled: false,
					loginResult: new ResultMessage(message, ResultStyle.Error)
				});

				console.log("Creating an account failed: " + reason);
			});
	}
}
